import { useCallback, useEffect } from 'react';

import { useInfiniteListConsumerFees } from '@givebacks/api/student';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { Center, Loader, LoadingOverlay, Stack } from 'ui';
import { useInViewport } from 'ui/Hooks';

import { FeeDisplayCard } from '../components/molecules/cards/feeDisplay/FeeDisplayCard';
import { NoFees } from '../components/molecules/no-data/NoFees';

const StudentFeesLoader = ({
  isFetchingNextPage,
  handleLoadMoreFees,
}: {
  isFetchingNextPage: boolean;
  handleLoadMoreFees: () => void;
}) => {
  const { ref, inViewport } = useInViewport();

  useEffect(() => {
    if (inViewport && !isFetchingNextPage) {
      handleLoadMoreFees();
    }
  }, [handleLoadMoreFees, inViewport, isFetchingNextPage]);

  return (
    <Center ref={ref} py="xl">
      {isFetchingNextPage && <Loader />}
    </Center>
  );
};

const FeesList = () => {
  const navigate = useNavigate();
  const {
    data: data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteListConsumerFees({
    enabled: true,
    queryOptions: {
      searches: {
        deleted: { value: false },
        status: { value: 'unpaid' },
      },
      join: 'AND',
    },
  });
  const handleLoadMoreFees = useCallback(() => {
    if (hasNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  if (!data?.pages[0].meta.count) {
    return <NoFees />;
  }

  return (
    <Sentry.ErrorBoundary fallback={<p>There was an error loading your fees</p>}>
      <Stack>
        {data.pages.map((page) =>
          page.student_fees.map((studentFee) => (
            <FeeDisplayCard
              key={studentFee.uuid}
              studentFee={studentFee}
              student={studentFee.student}
              withStudentName={true}
              onClick={() => navigate(`/student-fees/fees/${studentFee.uuid}`)}
            />
          ))
        )}
        <StudentFeesLoader
          isFetchingNextPage={isFetchingNextPage}
          handleLoadMoreFees={handleLoadMoreFees}
        />
      </Stack>
    </Sentry.ErrorBoundary>
  );
};

export { FeesList };
